import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Clock, Globe, Users, ArrowLeft, CheckCircle2, ArrowRight, Calendar, MapPin, Monitor, DollarSign, CalendarPlus, MapPinned, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { SEO, getCourseSchema, getBreadcrumbSchema } from '../components/SEO';
import { trackTrainingEnrollment, trackCourseView } from '../utils/analytics';

const API = '/api';

// Placeholder image
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=400&fit=crop';

export const CourseDetailPage = () => {
  const { t, language, direction } = useLanguage();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trainingCities, setTrainingCities] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestForm, setRequestForm] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    preferred_city: '',
    preferred_city_other: '',
    preferred_start_date: '',
    preferred_end_date: '',
    notes: ''
  });
  const [enrollForm, setEnrollForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    num_participants: 1,
    notes: ''
  });

  useEffect(() => {
    fetchCourse();
    fetchTrainingCities();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}`);
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingCities = async () => {
    try {
      const response = await fetch(`${API}/training-cities`);
      if (response.ok) {
        const data = await response.json();
        setTrainingCities(data);
      }
    } catch (error) {
      console.error('Error fetching training cities:', error);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const handleRequestChange = (field, value) => {
    setRequestForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...requestForm,
        course_id: course.course_id,
        course_name: language === 'ar' ? course.title_ar : course.title
      };
      
      const response = await fetch(`${API}/course-schedule-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(language === 'ar' ? 'تم إرسال طلبك بنجاح' : 'Your request has been submitted successfully');
        setRequestDialogOpen(false);
        setRequestForm({
          full_name: '',
          company: '',
          email: '',
          phone: '',
          preferred_city: '',
          preferred_city_other: '',
          preferred_start_date: '',
          preferred_end_date: '',
          notes: ''
        });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnrollChange = (field, value) => {
    setEnrollForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitEnrollment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        session_id: course.session_id || course.course_id,
        template_id: course.template_id || course.course_id,
        course_title: course.title,
        course_title_ar: course.title_ar || '',
        city: course.location || '',
        city_ar: course.location_ar || '',
        start_date: course.start_date || '',
        end_date: course.end_date || '',
        delivery_mode: course.delivery_mode || 'classroom',
        language: course.language || 'english',
        price: course.price || 0,
        currency: course.currency || 'EUR',
        ...enrollForm
      };
      
      const response = await fetch(`${API}/enrollment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(
          language === 'ar' 
            ? 'تم إرسال طلب التسجيل بنجاح. سيتواصل معك فريقنا قريباً' 
            : 'Enrollment request submitted successfully. Our team will contact you shortly.'
        );
        // Track enrollment in Google Analytics
        trackTrainingEnrollment(course?.course_id, course?.title);
        setEnrollDialogOpen(false);
        setEnrollForm({
          full_name: '',
          email: '',
          phone: '',
          company: '',
          num_participants: 1,
          notes: ''
        });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit enrollment request');
      }
    } catch (error) {
      toast.error('Failed to submit enrollment request');
    } finally {
      setSubmitting(false);
    }
  };

  const getCourseImage = () => {
    return course?.course_image || course?.image_url || PLACEHOLDER_IMAGE;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#111827] mb-4">Course not found</h2>
          <Link to="/courses">
            <Button className="btn-primary">Back to Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'ar' ? course.title_ar : course.title;
  const description = language === 'ar' ? course.description_ar : course.description;
  const objectives = language === 'ar' ? course.objectives_ar : course.objectives;
  const outline = language === 'ar' ? course.outline_ar : course.outline;
  const whoShouldAttend = language === 'ar' ? course.who_should_attend_ar : course.who_should_attend;
  const dailyOutline = course.daily_outline || [];
  const locationDisplay = language === 'ar' ? course.location_ar || course.location : course.location;
  const startDate = formatDate(course.start_date);
  const endDate = formatDate(course.end_date);

  const courseSchema = getCourseSchema(course, language);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الدورات التدريبية' : 'Training Courses', url: '/courses' },
    { name: title }
  ]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="course-detail-page">
      <SEO
        title={course.title}
        titleAr={course.title_ar}
        description={`${course.title} - ${course.duration_days || ''} day professional training course. ${course.delivery_mode} delivery. ${description?.slice(0, 120)}...`}
        descriptionAr={course.description_ar ? `${course.title_ar} - دورة تدريبية مهنية لمدة ${course.duration_days || ''} أيام. ${course.description_ar?.slice(0, 120)}...` : undefined}
        keywords={`${course.title}, training course, professional development, ${course.category || ''}, ${course.delivery_mode} training, دورة تدريبية`}
        canonicalUrl={`/courses/${course.course_id}`}
        structuredData={[courseSchema, breadcrumbSchema]}
        language={language}
      />
      {/* Hero Section with Course Image */}
      <section className="relative bg-[#111827]">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={getCourseImage()} 
            alt={title}
            className="w-full h-full object-cover opacity-30"
            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111827] via-[#111827]/90 to-[#111827]/70"></div>
        </div>
        
        <div className="container-custom relative z-10 py-12">
          <Link 
            to="/courses" 
            className="inline-flex items-center text-white/70 hover:text-white mb-6"
            data-testid="back-to-courses"
          >
            <ArrowLeft className={`h-4 w-4 me-2 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            {t('common.back')} {t('nav.courses')}
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="flex-1">
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-[#33AADD] text-white">
                  {course.delivery_mode === 'online' ? t('training.online') : 
                   course.delivery_mode === 'onsite' ? t('training.onsite') : t('training.hybrid')}
                </Badge>
                <Badge className="bg-white/20 text-white">
                  {course.language === 'english' ? t('training.english') : 
                   course.language === 'arabic' ? t('training.arabic') : t('training.bilingual')}
                </Badge>
                {course.is_featured && (
                  <Badge className="bg-yellow-500 text-white">Featured</Badge>
                )}
              </div>
              
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                {title}
              </h1>
              <p className="text-lg text-white/80 max-w-2xl">
                {description}
              </p>
              
              {/* Key Info Row */}
              <div className="flex flex-wrap gap-6 mt-6 text-white/90">
                {locationDisplay && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#33AADD]" />
                    <span>{locationDisplay}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#33AADD]" />
                  <span>{course.duration_days} {t('training.days')}</span>
                </div>
                {(startDate || endDate) && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[#33AADD]" />
                    <span>
                      {startDate && endDate ? `${startDate} — ${endDate}` : startDate || endDate}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Price Card */}
            <div className="lg:min-w-[280px]">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-6">
                  {course.price ? (
                    <div className="text-center mb-4">
                      <p className="text-sm text-white/70 mb-1">{t('training.price')}</p>
                      <p className="text-3xl font-bold text-white">
                        EUR {course.price.toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <p className="text-center text-lg text-white/90 mb-4">{t('training.requestQuote')}</p>
                  )}
                  <Button 
                    size="lg" 
                    className="w-full bg-[#33AADD] hover:bg-[#2899cc] text-white" 
                    data-testid="enroll-btn"
                    onClick={() => setEnrollDialogOpen(true)}
                  >
                    {language === 'ar' ? 'طلب التسجيل المبدئي' : 'Initial Enrollment Request'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Objectives */}
              {objectives?.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-4 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#33AADD]" />
                      {t('training.objectives')}
                    </h2>
                    <ul className="space-y-3">
                      {objectives.map((obj, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-[#6B7280]">{obj}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Outline */}
              {outline?.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-4 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-[#33AADD]" />
                      {t('training.outline')}
                    </h2>
                    <ul className="space-y-3">
                      {outline.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0 text-sm font-medium text-[#33AADD]">
                            {index + 1}
                          </span>
                          <span className="text-[#6B7280]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Daily Breakdown */}
              {dailyOutline?.length > 0 && (
                <Card data-testid="daily-outline-section">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-[#111827] mb-6 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-[#33AADD]" />
                      {language === 'ar' ? 'الجدول اليومي التفصيلي' : 'Detailed Daily Schedule'}
                    </h2>
                    <div className="space-y-6">
                      {dailyOutline.map((day, index) => (
                        <div key={index} className="border-s-4 border-[#33AADD] ps-4" data-testid={`day-${day.day}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#33AADD] text-white text-sm font-bold">
                              {day.day}
                            </span>
                            <h3 className="text-lg font-semibold text-[#111827]">
                              {language === 'ar' ? `اليوم ${day.day}` : `Day ${day.day}`}
                              {(language === 'ar' ? day.title_ar : day.title) && (
                                <span className="font-normal text-[#6B7280]"> — {language === 'ar' ? day.title_ar : day.title}</span>
                              )}
                            </h3>
                          </div>
                          {((language === 'ar' ? day.topics_ar : day.topics) || []).length > 0 && (
                            <ul className="space-y-2 ms-10">
                              {(language === 'ar' ? day.topics_ar : day.topics).map((topic, topicIndex) => (
                                <li key={topicIndex} className="flex items-start gap-2 text-[#6B7280]">
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span>{topic}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Who Should Attend */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-[#111827] mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#33AADD]" />
                    {t('training.whoShouldAttend')}
                  </h2>
                  <p className="text-[#6B7280] whitespace-pre-line">{whoShouldAttend}</p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Course Info */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">
                    {language === 'ar' ? 'تفاصيل الدورة' : 'Course Details'}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                        <Clock className="h-5 w-5 text-[#33AADD]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{t('training.duration')}</p>
                        <p className="font-medium text-[#111827]">
                          {course.duration_days} {t('training.days')}
                          {course.duration_hours && ` (${course.duration_hours} ${t('training.hours')})`}
                        </p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                        <Monitor className="h-5 w-5 text-[#33AADD]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{t('training.mode')}</p>
                        <p className="font-medium text-[#111827]">
                          {course.delivery_mode === 'online' ? t('training.online') : 
                           course.delivery_mode === 'onsite' ? t('training.onsite') : t('training.hybrid')}
                        </p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                        <Globe className="h-5 w-5 text-[#33AADD]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{t('training.language')}</p>
                        <p className="font-medium text-[#111827]">
                          {course.language === 'english' ? t('training.english') : 
                           course.language === 'arabic' ? t('training.arabic') : t('training.bilingual')}
                        </p>
                      </div>
                    </div>
                    
                    {locationDisplay && (
                      <>
                        <Separator />
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-[#33AADD]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'الموقع' : 'Location'}</p>
                            <p className="font-medium text-[#111827]">{locationDisplay}</p>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {(startDate || endDate) && (
                      <>
                        <Separator />
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-[#33AADD]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                            <p className="font-medium text-[#111827]">
                              {startDate && endDate ? (
                                <>
                                  {startDate}
                                  <br />
                                  <span className="text-[#6B7280]">to</span> {endDate}
                                </>
                              ) : startDate || endDate}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Request Different City/Date */}
              <Card className="border-2 border-dashed border-[#33AADD]/30 bg-[#33AADD]/5">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-[#33AADD]/10 flex items-center justify-center mx-auto mb-3">
                      <CalendarPlus className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#111827] mb-2">
                      {language === 'ar' ? 'هل تحتاج موعد أو مدينة أخرى؟' : 'Need a Different Date or City?'}
                    </h3>
                    <p className="text-sm text-[#6B7280] mb-4">
                      {language === 'ar' 
                        ? 'يمكننا تنظيم هذه الدورة في مدينة أخرى أو في تواريخ مختلفة'
                        : 'We can arrange this course in another city or on different dates'}
                    </p>
                    <Button 
                      onClick={() => setRequestDialogOpen(true)}
                      className="w-full btn-primary"
                      data-testid="request-schedule-btn"
                    >
                      <MapPinned className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'طلب موعد / مدينة أخرى' : 'Request Another Date/City'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Enroll CTA */}
              <Card className="bg-[#33AADD] border-none">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {language === 'ar' ? 'جاهز للتسجيل؟' : 'Ready to enroll?'}
                  </h3>
                  <p className="text-white/90 text-sm mb-4">
                    {language === 'ar' ? 'احجز مقعدك في هذه الدورة!' : 'Reserve your spot in this course!'}
                  </p>
                  <Button 
                    variant="secondary" 
                    className="w-full bg-white text-[#33AADD] hover:bg-white/90" 
                    data-testid="sidebar-enroll-btn"
                    onClick={() => setEnrollDialogOpen(true)}
                  >
                    {language === 'ar' ? 'طلب التسجيل المبدئي' : 'Initial Enrollment Request'}
                  </Button>
                </CardContent>
              </Card>

              {/* Download Course Outline */}
              <Card className="border-[#33AADD]/20 bg-gradient-to-br from-white to-[#33AADD]/5">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#111827] mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#33AADD]" />
                    {language === 'ar' ? 'محتوى الدورة' : 'Course Outline'}
                  </h3>
                  <p className="text-sm text-[#6B7280] mb-4">
                    {language === 'ar' 
                      ? 'قم بتنزيل الكتيب الكامل للدورة بصيغة PDF'
                      : 'Download the complete course brochure as PDF'}
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full border-[#33AADD] text-[#33AADD] hover:bg-[#33AADD] hover:text-white"
                    data-testid="download-outline-btn"
                    onClick={() => {
                      window.open(`${API}/courses/${courseId}/outline-pdf`, '_blank');
                      toast.success(language === 'ar' ? 'جاري تنزيل الكتيب...' : 'Downloading course outline...');
                    }}
                  >
                    <Download className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'تنزيل الكتيب PDF' : 'Download Outline PDF'}
                  </Button>
                </CardContent>
              </Card>

              {/* Custom Training */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#111827] mb-2">
                    {t('training.customTraining')}
                  </h3>
                  <p className="text-sm text-[#6B7280] mb-4">
                    {t('training.customTrainingDesc')}
                  </p>
                  <Link to="/customized-training-request">
                    <Button variant="outline" className="w-full" data-testid="custom-training-sidebar-btn">
                      {t('training.requestCustom')}
                      <ArrowRight className={`ms-2 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Request Schedule Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'طلب موعد / مدينة أخرى' : 'Request Another Date or City'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div className="p-3 bg-[#F9FAFB] rounded-lg">
              <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'الدورة' : 'Course'}</p>
              <p className="font-medium text-[#111827]">{title}</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">{language === 'ar' ? 'الاسم الكامل' : 'Full Name'} *</Label>
                <Input
                  id="full_name"
                  value={requestForm.full_name}
                  onChange={(e) => handleRequestChange('full_name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">{language === 'ar' ? 'الشركة' : 'Company'}</Label>
                <Input
                  id="company"
                  value={requestForm.company}
                  onChange={(e) => handleRequestChange('company', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={requestForm.email}
                  onChange={(e) => handleRequestChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{language === 'ar' ? 'الهاتف' : 'Phone'} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={requestForm.phone}
                  onChange={(e) => handleRequestChange('phone', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'المدينة المفضلة' : 'Preferred City'}</Label>
              <Select 
                value={requestForm.preferred_city} 
                onValueChange={(v) => handleRequestChange('preferred_city', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'ar' ? 'اختر مدينة' : 'Select a city'} />
                </SelectTrigger>
                <SelectContent>
                  {trainingCities.map((city) => (
                    <SelectItem key={city.city_id} value={language === 'ar' ? city.name_ar : city.name}>
                      {language === 'ar' ? city.name_ar : city.name}
                      {city.country && ` (${language === 'ar' ? city.country_ar || city.country : city.country})`}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">{language === 'ar' ? 'أخرى' : 'Other'}</SelectItem>
                </SelectContent>
              </Select>
              {requestForm.preferred_city === 'other' && (
                <Input
                  placeholder={language === 'ar' ? 'حدد المدينة' : 'Specify city'}
                  value={requestForm.preferred_city_other}
                  onChange={(e) => handleRequestChange('preferred_city_other', e.target.value)}
                  className="mt-2"
                />
              )}
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="preferred_start_date">{language === 'ar' ? 'تاريخ البداية المفضل' : 'Preferred Start Date'}</Label>
                <Input
                  id="preferred_start_date"
                  type="date"
                  value={requestForm.preferred_start_date}
                  onChange={(e) => handleRequestChange('preferred_start_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferred_end_date">{language === 'ar' ? 'تاريخ النهاية المفضل' : 'Preferred End Date'}</Label>
                <Input
                  id="preferred_end_date"
                  type="date"
                  value={requestForm.preferred_end_date}
                  onChange={(e) => handleRequestChange('preferred_end_date', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">{language === 'ar' ? 'ملاحظات إضافية' : 'Additional Notes'}</Label>
              <Textarea
                id="notes"
                value={requestForm.notes}
                onChange={(e) => handleRequestChange('notes', e.target.value)}
                rows={3}
                placeholder={language === 'ar' ? 'أي متطلبات أو ملاحظات خاصة...' : 'Any special requirements or notes...'}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button type="submit" className="btn-primary" disabled={submitting}>
                {submitting 
                  ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
                  : (language === 'ar' ? 'إرسال الطلب' : 'Submit Request')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enrollment Request Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'طلب التسجيل المبدئي' : 'Initial Enrollment Request'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEnrollment} className="space-y-4">
            {/* Course Context (Pre-filled) */}
            <div className="p-4 bg-[#F0F9FF] border border-[#33AADD]/20 rounded-lg space-y-2">
              <h4 className="font-semibold text-[#111827]">{title}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-[#6B7280]">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-[#33AADD]" />
                  <span>{locationDisplay || (language === 'ar' ? 'حسب الطلب' : 'TBD')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Monitor className="h-4 w-4 text-[#33AADD]" />
                  <span>{course.delivery_mode === 'online' ? (language === 'ar' ? 'عبر الإنترنت' : 'Online') : 
                         course.delivery_mode === 'hybrid' ? (language === 'ar' ? 'هجين' : 'Hybrid') : 
                         (language === 'ar' ? 'حضوري' : 'Classroom')}</span>
                </div>
                {(startDate || endDate) && (
                  <div className="flex items-center gap-1 col-span-2">
                    <Calendar className="h-4 w-4 text-[#33AADD]" />
                    <span>{startDate && endDate ? `${startDate} — ${endDate}` : startDate || endDate}</span>
                  </div>
                )}
                {course.price && (
                  <div className="flex items-center gap-1 col-span-2">
                    <DollarSign className="h-4 w-4 text-[#33AADD]" />
                    <span className="font-semibold text-[#111827]">EUR {course.price.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* User Details */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="enroll_full_name">{language === 'ar' ? 'الاسم الكامل' : 'Full Name'} *</Label>
                <Input
                  id="enroll_full_name"
                  value={enrollForm.full_name}
                  onChange={(e) => handleEnrollChange('full_name', e.target.value)}
                  required
                  data-testid="enroll-full-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enroll_email">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *</Label>
                <Input
                  id="enroll_email"
                  type="email"
                  value={enrollForm.email}
                  onChange={(e) => handleEnrollChange('email', e.target.value)}
                  required
                  data-testid="enroll-email"
                />
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="enroll_phone">{language === 'ar' ? 'رقم الهاتف' : 'Phone'} *</Label>
                <Input
                  id="enroll_phone"
                  value={enrollForm.phone}
                  onChange={(e) => handleEnrollChange('phone', e.target.value)}
                  required
                  data-testid="enroll-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enroll_company">{language === 'ar' ? 'الشركة' : 'Company'}</Label>
                <Input
                  id="enroll_company"
                  value={enrollForm.company}
                  onChange={(e) => handleEnrollChange('company', e.target.value)}
                  data-testid="enroll-company"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="enroll_participants">{language === 'ar' ? 'عدد المشاركين' : 'Number of Participants'}</Label>
              <Input
                id="enroll_participants"
                type="number"
                min="1"
                max="50"
                value={enrollForm.num_participants}
                onChange={(e) => handleEnrollChange('num_participants', parseInt(e.target.value) || 1)}
                data-testid="enroll-participants"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="enroll_notes">{language === 'ar' ? 'ملاحظات إضافية' : 'Additional Notes'}</Label>
              <Textarea
                id="enroll_notes"
                value={enrollForm.notes}
                onChange={(e) => handleEnrollChange('notes', e.target.value)}
                rows={3}
                placeholder={language === 'ar' ? 'أي متطلبات خاصة أو أسئلة...' : 'Any special requirements or questions...'}
                data-testid="enroll-notes"
              />
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="enroll-submit">
                {submitting 
                  ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
                  : (language === 'ar' ? 'إرسال طلب التسجيل' : 'Submit Enrollment Request')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseDetailPage;
